import apiClient from './apiClient';

// Define interfaces for game and league data
import type { Game, League } from '../components/GameLeagueSelector';

// Cache data structure
interface GameLeagueCache {
  timestamp: number;
  games: Game[];
  leagues: League[];
}

// Cache object to store the data
let cache: GameLeagueCache | null = null;

// Cache duration - 10 minutes in milliseconds
const CACHE_DURATION = 10 * 60 * 1000;

/**
 * Fetches unique game/league combinations from the characters table
 * and formats them into structured Game and League objects
 * with caching for 10 minutes
 */
export const getGamesAndLeagues = async (): Promise<{ games: Game[]; leagues: League[] }> => {
  try {
    // Check if we have cached data that's still valid
    const currentTime = Date.now();
    if (cache && currentTime - cache.timestamp < CACHE_DURATION) {
      return {
        games: cache.games,
        leagues: cache.leagues,
      };
    }

    // Fetch fresh data if cache is invalid or missing
    const response = await apiClient.get<Array<{ game: string; league: string }>>('/characters/game-league-combinations');

    // Process the returned data to create Game and League objects
    const gameMap = new Map<string, Game>();
    const leagues: League[] = [];
    let gameIdCounter = 1;
    let leagueIdCounter = 1;

    // Process each combination to build the Game and League objects
    response.data.forEach(combination => {
      const { game: gameCode, league: leagueCode } = combination;

      // Format game name (e.g., "PoE1" -> "Path of Exile 1")
      const gameName = gameCode === 'PoE1' ? 'Path of Exile 1' : 'Path of Exile 2';

      // Add game if not already in the map
      if (!gameMap.has(gameCode)) {
        gameMap.set(gameCode, {
          id: String(gameIdCounter++),
          name: gameName,
          code: gameCode,
          isActive: true,
        });
      }

      // Get the game's ID for the league reference
      const gameId = gameMap.get(gameCode)!.id;

      // Create a league object
      // Format the league name for display (first letter uppercase, rest lowercase)
      const leagueName = leagueCode.charAt(0).toUpperCase() + leagueCode.slice(1).toLowerCase();

      // Add the league
      leagues.push({
        id: String(leagueIdCounter++),
        name: leagueName,
        code: leagueCode,
        gameId,
        isActive: true,
      });
    });

    // Convert the map to an array for games
    const games = Array.from(gameMap.values());

    // Update the cache
    cache = {
      timestamp: currentTime,
      games,
      leagues,
    };

    return { games, leagues };
  } catch (error: any) {
    console.error('Error fetching game/league combinations:', error);
    // Return empty arrays in case of error
    return { games: [], leagues: [] };
  }
};

/**
 * Force refresh the cache by clearing it
 */
export const refreshGamesAndLeagues = () => {
  cache = null;
};
